import React, { useState, useRef } from 'react'
import { Card, CardHeader, CardBody, Table, Input, Button } from 'reactstrap'
import { API_URL } from '../utils/Constants/URLS'

const StockSearch = () => {
  const token = localStorage.getItem('token')
  const [query, setQuery] = useState('')
  const [docs, setDocs] = useState([])
  const [error, setError] = useState(null)
  const [searchPerformed, setSearchPerformed] = useState(false)
  const abortControllerRef = useRef(null)

  const fetchData = async search => {
    // abort previous request if any
    if (abortControllerRef.current) abortControllerRef.current.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    setError(null)

    try {
      const res = await fetch(
        `${API_URL}models/batches/aggregate?search=${encodeURIComponent(
          search
        )}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : ''
          },
          signal: controller.signal
        }
      )

      if (!res.ok) {
        const text = await res.text()
        throw new Error(`Server responded ${res.status}: ${text}`)
      }

      const result = await res.json()
      const docs = Array.isArray(result?.data?.docs) ? result.data.docs : []
      setDocs(docs)
    } catch (err) {
      if (err.name === 'AbortError') {
        // ignore abort
      } else {
        setError(err.message || 'Failed to fetch')
        setDocs([])
      }
    }
  }

  const handleSearch = e => {
    e.preventDefault()
    setSearchPerformed(true)
    setDocs([]) // clear prior results until fetch resolves
    setError(null)
    if (!query || !query.trim()) {
      // keep headers-only table if empty query
      return
    }
    fetchData(query)
  }

  return (
    <Card>
      <CardHeader>Stock Search</CardHeader>
      <CardBody>
        <form onSubmit={handleSearch} className='flex gap-3 items-center mb-4'>
          <Input
            type='text'
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder='Search model name or part code...'
            className='flex-1'
          />
          <Button type='submit' className='btn add-btn text-white'>
            Search
          </Button>
        </form>

        {/* Error message (if any) */}
        {error && <div className='text-danger mb-2'>{error}</div>}

        {/* Table: always render header. Rows will appear only after search and data */}
        <div className='overflow-auto'>
          <Table striped responsive>
            <thead>
              <tr>
                <th>Model Name</th>
                <th>Part Code</th>
                <th>Store Name</th>
                <th>List Price</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {/* If user didn't perform search yet, keep tbody empty (header-only) */}
              {searchPerformed &&
                docs.length === 0 &&
                // no rows while awaiting results or when results empty
                null}

              {docs.map(d => {
                const model = d.model ?? {}
                const store = d.store ?? {}
                const key = `${model._id || 'm'}_${store._id || 's'}`

                return (
                  <tr key={key}>
                    <td>{model.modelName || '-'}</td>
                    <td>{model.partCode || '-'}</td>
                    <td>{store.name || '-'}</td>
                    <td>{d.listPrice ?? '-'}</td>
                    <td>{d.totalQuantity ?? 0}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  )
}

export default StockSearch
